import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ISizeName } from '@df/core/service/client';
import { StylesService } from '@df/core/service/styles';
import { DfImageWrapperComponent } from '@df/module-image/df-image-wrapper.component';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { Customer } from '@df/session/api/customer';
import { IfLoggedInDirective } from '@df/session/customer/if-logged-in.directive';
import { IfNotLoggedInDirective } from '@df/session/customer/if-not-logged-in.directive';
import { RatioOptions } from '@df/ui/atomic/atomic-options';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalModule } from '@df/ui/modal/modal.module';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ActionComponent } from '../../../ui/api/action.component';
import { LoginFormContentComponent } from '../../content/login-form-content.component';
interface ILoginFormModalData extends Catalog.IBlockData {
    image: string;
    imageRatio: string;
    title: string;
    description: string;
    loggedInDescription: string;
    loggedInTitle: string;
    loggedInCtaLabel: string;
}
export const LOGIN_FORM_MODAL_ID = 'login-form-modal';
@DfCmsModal({
    id: LOGIN_FORM_MODAL_ID,
    position: 'right',
    name: 'Login Form',
    config: (sizeId: ISizeName) => ({
        width: sizeId === 'S' ? 'calc(100vw - 20px)' : '600px',
        maxWidth: StylesService.vw(100),
        height: '100%',
        minHeight: StylesService.vh(100),
        maxHeight: StylesService.vh(100),
        position: {
            top: '0',
            right: '0',
            bottom: '0'
        },
        modalClass: 'p-a-0 bg-col-51',
        closeOnNavigation: true
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: sizeId => (sizeId === 'S' ? ['S'] : ['M', 'L', 'X'])
})
@ICmsComponent<LoginFormModalComponent>({
    id: LOGIN_FORM_MODAL_ID,
    block: {
        url: LOGIN_FORM_MODAL_ID,
        autoload: 'bootstrap'
    },
    onDemandOnly: true,
    name: 'Login Form Popup',
    fields: [
        <NICmsContentField.IField>{
            key: 'image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image',
            required: true,
            default: '/cms/media/default.png'
        },
        <NICmsContentField.IChoiceField>{
            key: 'imageRatio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Image Ratio',
            options: RatioOptions,
            required: true,
            default: 'ratio-2-1'
        },
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            required: true,
            default: 'Wishlist'
        },
        <NICmsContentField.IField>{
            key: 'description',
            type: NICmsContentField.EFieldType.text,
            title: 'Description',
            required: true,
            default: 'Sign up to add items to your wishlist'
        },
        <NICmsContentField.IField>{
            key: 'loggedInTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Logged in title',
            required: true,
            default: 'You are signed in'
        },
        <NICmsContentField.IField>{
            key: 'loggedInDescription',
            type: NICmsContentField.EFieldType.text,
            title: 'Logged in description',
            required: true,
            default: 'You can view recent orders in your account, add items to your wishlist and enjoy quicker checkout'
        },
        <NICmsContentField.IField>{
            key: 'loggedInCtaLabel',
            type: NICmsContentField.EFieldType.text,
            title: 'Logged in CTA label',
            required: true,
            default: 'Continue shopping'
        }
    ]
})
@Component({
    selector: 'login-form-modal',
    templateUrl: './login-form-modal.html',
    standalone: true,
    imports: [
        ActionComponent,
        ICmsClientModule,
        IfLoggedInDirective,
        IfNotLoggedInDirective,
        LoginFormContentComponent,
        ModalModule,
        DfImageWrapperComponent,
        DfImageDirective,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginFormModalComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: ILoginFormModalData;
    @Input()
    variant?: string;

    constructor(
        readonly customer: Customer,
        private modalRef: ModalRef<any>
    ) {
        super();
    }

    override ngOnInit(): void {
        this.subscriptions.add(this.customer.model.obs.status.subscribe(() => this.detectChanges()));
        super.ngOnInit();
    }

    protected close() {
        this.modalRef?.close();
    }
}
